import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

// Auth Context
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/Auth/ProtectedRoute'

// System Services
import SystemAPI from './services/system'

// Lazy loader that reloads once on a stale-chunk error after a deploy
import lazyWithRetry from './utils/lazyWithRetry'

// Layouts
import MainLayout from './components/Layout/MainLayout'

// Pages - Lazy loaded for better performance
const Landing = lazyWithRetry(() => import('./pages/Landing'))
const Pricing = lazyWithRetry(() => import('./pages/Pricing'))
const Auth = lazyWithRetry(() => import('./pages/Auth'))
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'))
const PostRegistrationSetup = lazyWithRetry(() => import('./components/PostRegistrationSetup/PostRegistrationSetup'))
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const CategoryTuning = lazyWithRetry(() => import('./pages/CategoryTuning'))
const Billing = lazyWithRetry(() => import('./pages/Billing'))
const BillingSuccess = lazyWithRetry(() => import('./pages/BillingSuccess'))
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'))
const Privacy = lazyWithRetry(() => import('./pages/Privacy'))
const Terms = lazyWithRetry(() => import('./pages/Terms'))

// Auth components
const ResetPassword = lazyWithRetry(() => import('./components/Auth/ResetPassword'))

// Integration and AI pages
const Integrations = lazyWithRetry(() => import('./pages/Integrations'))
const IntegrationCallback = lazyWithRetry(() => import('./pages/IntegrationCallback'))
const OptimizationDashboard = lazyWithRetry(() => import('./components/AI/OptimizationDashboard'))

// Admin pages
const AdminDashboard = lazyWithRetry(() => import('./pages/Admin/AdminDashboard'))
const UserManagement = lazyWithRetry(() => import('./pages/Admin/UserManagement'))
const SystemConfig = lazyWithRetry(() => import('./pages/Admin/SystemConfig'))

// Error Boundary
import ErrorBoundary from './components/Common/ErrorBoundary'

// Loading component
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-secondary-600 font-medium">Loading...</p>
    </div>
  </div>
)

// Route animation variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
}

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3,
}

function App() {
  // Initialize system services on app startup
  useEffect(() => {
    const initializeSystem = async () => {
      try {
        await SystemAPI.initialize()
      } catch (error) {
        console.error('System initialization error:', error)
        // Don't block app startup if system initialization fails
      }
    }
    
    initializeSystem()
  }, [])

  return (
    <AuthProvider>
      <ErrorBoundary>
        <div className="min-h-screen bg-secondary-50">
          <AnimatePresence mode="wait">
            <Routes>
            {/* Landing Page (Public) */}
            <Route path="/" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Landing />
                </motion.div>
              </Suspense>
            } />

            {/* Pricing Page (Public) */}
            <Route path="/pricing" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Pricing />
                </motion.div>
              </Suspense>
            } />

            {/* Authentication Route */}
            <Route path="/auth" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Auth />
                </motion.div>
              </Suspense>
            } />

            {/* Password Reset Route */}
            <Route path="/reset-password" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <ResetPassword />
                </motion.div>
              </Suspense>
            } />

            {/* Legal Pages (Public) */}
            <Route path="/privacy" element={
              <Suspense fallback={<PageLoading />}>
                <Privacy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<PageLoading />}>
                <Terms />
              </Suspense>
            } />

            {/* Legacy routes redirect to setup */}
            <Route path="/quickstart" element={<Navigate to="/setup" replace />} />
            <Route path="/onboarding" element={<Navigate to="/setup" replace />} />

            {/* Email Verification Route */}
            <Route path="/verify-email" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <VerifyEmail />
                </motion.div>
              </Suspense>
            } />

            {/* Post-Registration Setup Route */}
            <Route path="/setup" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <PostRegistrationSetup />
                  </motion.div>
                </Suspense>
              </ProtectedRoute>
            } />

            {/* Billing Success Route */}
            <Route path="/billing/success" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <BillingSuccess />
                </motion.div>
              </Suspense>
            } />

            {/* Integration OAuth Callback Routes */}
            <Route path="/integrations/:provider/:status" element={
              <Suspense fallback={<PageLoading />}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <IntegrationCallback />
                </motion.div>
              </Suspense>
            } />

            {/* Main Application Routes (Protected) */}
            <Route path="/app" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              {/* Dashboard */}
              <Route index element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Dashboard />
                  </motion.div>
                </Suspense>
              } />



              {/* Settings */}
              <Route path="settings" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Settings />
                  </motion.div>
                </Suspense>
              } />

              {/* Category Tuning */}
              <Route path="category-tuning" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <CategoryTuning />
                  </motion.div>
                </Suspense>
              } />

              {/* Billing */}
              <Route path="billing" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Billing />
                  </motion.div>
                </Suspense>
              } />

              {/* Integrations */}
              <Route path="integrations" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Integrations />
                  </motion.div>
                </Suspense>
              } />

              {/* AI Optimization */}
              <Route path="ai-optimization" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <OptimizationDashboard />
                  </motion.div>
                </Suspense>
              } />

              {/* Admin Routes */}
              <Route path="admin" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <AdminDashboard />
                  </motion.div>
                </Suspense>
              } />
              
              <Route path="admin/users" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <UserManagement />
                  </motion.div>
                </Suspense>
              } />
              
              <Route path="admin/system" element={
                <Suspense fallback={<PageLoading />}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <SystemConfig />
                  </motion.div>
                </Suspense>
              } />
            </Route>

            {/* Redirect unknown routes to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AnimatePresence>
        </div>
      </ErrorBoundary>
    </AuthProvider>
  )
}

export default App